/**
 * @file useDeleteEntry.js
 * @module src/js/hooks/useDeleteEntry
 * @description
 * **A hook to delete an entry from a specified endpoint.**
 * - Utilizes React contexts to manage user state and modal messages.
 * - Returns a function that:
 *   - Sends a DELETE request to the backend to remove the entry.
 *   - Updates the data in the frontend upon successful deletion.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add modal messages. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useFetchFromBackend. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import { useModal } from "../contexts/ModalProvider";
import { useTheme } from "../contexts/ThemeProvider";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useDeleteEntry({
    setData,
    setDeleting,
}) {
    const { theme } = useTheme();
    const { showModalMessage, confirm } = useModal();
    const fetchFromBackend = useFetchFromBackend();

    if (typeof setData !== 'function' || typeof setDeleting !== 'function') {
        throw new Error("setData and setDeleting must be functions.");
    }

    const deleteEntry = useCallback(async ({
        request,
        id,
        from,
        scrollTo,
    }) => {
        if (!request || !id) {
            throw new Error("Both request and id are required.");
        }
        
        try {
            setDeleting(true);
            
            // Ask the user first
            const ok = await confirm({
                message: "Are you sure you want to delete this entry?\nOnly the administrator can undo this action.",
                confirmLabel: "Delete",
                cancelLabel: "Cancel",
                buttonColor: theme === "dark" ? "lightcoral" : "red",
            });

            if (!ok) {
                showModalMessage({ type: "success", content: "Cancelled." });
                return;
            }

            // User confirmed, now perform delete
            showModalMessage({ type: "info", content: "Deleting..." });

            const data = await fetchFromBackend({
                request,
                method: "DELETE",
                body: { id },
                requireAuth: true,
                from,
                scrollTo,
            });

            if (data && data.success) {
                const data = await fetchFromBackend({ request });
                setData(data || []);
                showModalMessage({ type: "success", content: "Successfully deleted!" });


            } else if (data && data.error) {
                console.error("Failed to delete:", data.error);
                showModalMessage({ type: "error", content: "Failed to delete. Please try again." });
            }

        } catch (error) {
            console.error("An error occurred:", error);
            showModalMessage({ type: "error", content: "An error occurred. Please try again." });

        } finally {
            setDeleting(false);
        }

    }, [fetchFromBackend, setData, setDeleting, showModalMessage, confirm, theme]);

    return deleteEntry;
}